import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { omit } from 'lodash';
import { Button, Loading } from '@brighterion/br-ui';
import { RULE_ACTION_TYPE } from 'Common/enums';
import { handleKeyEvent, handleOnClickEvent } from 'Utils';
import PageHeader from 'Components/PageHeader';
import appStyles from 'Routes/Routes.css';
import RuleTester from 'Components/AddRulePage/RuleTester/RuleTester';
import { shouldShowRuleTester } from 'Selectors';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import EditRuleInfoOverview from '../EditRuleInfoOverview';
import RuleStatement from '../RuleStatement';
import { simpleRuleValidationSchema } from './simpleRuleValidationSchema';

import styles from './EditSimpleRuleForm.css';

type EditSimpleRuleFormProps = {
    parameterData?: UseGetParametersResponse | undefined;
    conclusionFlagListData?: ConclusionListApiGetResponse[];
    disabledInputFields?: DisabledInputFields;
    formRef?: any;
    globalRule: Rule;
    listData?: ListApiGetResponse;
    mutation: any;
    overrideRule?: Rule;
    pageHeader: string;
    ruleId: string;
    rulesType: RulesType;
    selectedRowId: string;
    subjectIdentifier?: string;
    actionType: OverrideAction;
    overrideLevel?: OverrideLevel;
};

const EditSimpleRuleForm = ({
    parameterData,
    conclusionFlagListData,
    disabledInputFields,
    formRef,
    globalRule,
    listData,
    mutation,
    overrideRule,
    pageHeader,
    ruleId,
    rulesType,
    selectedRowId,
    subjectIdentifier,
    actionType,
    overrideLevel
}: EditSimpleRuleFormProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openModal, setOpenModal] = useState(false);
    const isTenantOverride = rulesType === 'tenantOverrides';
    const currentRule =
        rulesType === 'merchantOverrides' || rulesType === 'tenantOverrides' || rulesType === 'tenantGroupOverrides'
            ? overrideRule
            : globalRule;
    const handleCancel = () => {
        switch (rulesType) {
            case 'globalRules':
                history.push(`/global-rules?selectedRowId=${selectedRowId}`);
                break;
            case 'merchantOverrides':
                history.push(`/overrides/merchant/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;
            case 'tenantGroupOverrides':
                history.push(`/tenants/tenant-group/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;

            default:
                // tenant overrides
                history.push('/global-rules');
                break;
        }
    };

    const handleTrackEventClick = () => {
        let clickIdentifier: string | undefined;
        let text: string | undefined;

        if (rulesType === 'tenantGroupOverrides') {
            if (actionType === 'add') {
                clickIdentifier = 'Tenant Groups - Add Override';
                text = 'Add Override';
            } else if (actionType === 'edit') {
                clickIdentifier = 'Tenant Groups - Edit Override';
                text = 'Edit Override';
            }
        } else if (rulesType === 'tenantOverrides') {
            if (actionType === 'add') {
                clickIdentifier = 'Global Rules - Add Tenant Override';
                text = 'Add Tenant Override';
            } else if (actionType === 'edit') {
                clickIdentifier = 'Global Rules - Edit Tenant Override';
                text = 'Edit Tenant Override';
            }
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    /** @note TemplatedRuleFormValues don't have types. Most values are type any. */
    const handleSave = (formValues: TemplatedRuleFormValues) => {
        const { lists, comment, actions, ...rest } = formValues;
        const parameters = omit(rest, 'start', 'end', 'conditions');
        actions?.forEach((action) => {
            /** If ACCEPT, REJECT or REVIEW delete value from values */
            if (
                [RULE_ACTION_TYPE.ACCEPT, RULE_ACTION_TYPE.REJECT, RULE_ACTION_TYPE.REVIEW].includes(
                    action.operator as RULE_ACTION_TYPE
                )
            ) {
                action.values = [''];
            }
            /** Convert number values in string[] */
            if (typeof action.values === 'number') {
                action.values = [String(action.values)];
            }
            /** Convert string values in string[] */
            if (typeof action.values === 'string') {
                action.values = [action.values];
            }
        });

        mutation.mutate({
            rules: [
                {
                    id: ruleId,
                    actions,
                    parameters: {
                        ...parameters
                    },
                    inputs: currentRule?.inputs,
                    lists,
                    nonTemplated: currentRule?.nonTemplated
                }
            ],
            comments: {
                [ruleId]: comment
            }
        });

        handleTrackEventClick();
    };

    /** @note Make sure all the data is available before rendering the Form */
    if (!currentRule) return <Loading />;

    /**
     * @note Remove landlord List Ids values if it's a Network Override.
     *
     * The tenant is not sharing the same List Ids with the landlord,
     * but a merchant will have the same List Ids as the tenant.
     */
    if (isTenantOverride) {
        currentRule.actions?.forEach((action) => {
            if (action.operator === RULE_ACTION_TYPE.ADD_TO_LIST) {
                const listIdExist = (action.values as AddToList).listIds.some((id) => {
                    if (listData?.listIdHashMap[Number(id)]) {
                        return true;
                    }
                    return false;
                });
                if (!listIdExist) {
                    (action.values as AddToList).listIds = [];
                }
            }
        });
    }

    const formikInitialValues = {
        actions: currentRule.actions,
        ...currentRule.parameters,
        ...(currentRule.lists && { lists: currentRule.lists }),
        comment: ''
    };

    return (
        <Formik
            enableReinitialize
            initialValues={formikInitialValues}
            onSubmit={(formValues) => handleSave(formValues)}
            validationSchema={simpleRuleValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { dirty, isValid, values, isSubmitting, handleSubmit } = formikProps;
                const updatedValues = { ...values, ruleId };
                const updatedFormikProps = { ...formikProps, values: updatedValues };
                const disableSave = !dirty || !isValid;

                const submitForm = () => {
                    handleSubmit();
                };

                return (
                    <div className={appStyles.landingPage}>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>
                                {pageHeader}
                                {!currentRule?.nonTemplated && (
                                    <span className={styles.ruleTypeText}>{t('DEFAULT RULE')}</span>
                                )}
                            </PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                <Button
                                    disabled={isSubmitting}
                                    dataTestId='editForm-cancel-btn'
                                    variant='tertiary'
                                    size='medium'
                                    onKeyDown={handleKeyEvent(handleCancel)}
                                    onClick={handleOnClickEvent(handleCancel)}
                                >
                                    {t('Cancel')}
                                </Button>

                                <Button
                                    dataTestId='editForm-save-btn'
                                    variant='primary'
                                    size='small'
                                    onKeyDown={handleKeyEvent(submitForm)}
                                    onClick={handleOnClickEvent(submitForm)}
                                    disabled={disableSave || isSubmitting}
                                >
                                    {isSubmitting ? t('Saving...') : t('Save Rule')}
                                </Button>
                            </PageHeader.Right>
                        </PageHeader>
                        <EditRuleInfoOverview
                            currentGlobalRule={globalRule!}
                            currentOverrideRule={overrideRule!}
                            listData={listData}
                            rulesType={rulesType}
                            isSubmitting={formikProps.isSubmitting}
                            openModal={openModal}
                            setOpenModal={setOpenModal}
                            subjectIdentifier={subjectIdentifier}
                        />
                        <RuleStatement
                            currentRule={currentRule!}
                            parameterData={parameterData}
                            listData={listData}
                            mutationError={mutation.error}
                            disabledInputFields={disabledInputFields}
                            conclusionFlagListData={conclusionFlagListData}
                        />
                        {shouldShowRuleTester() && (
                            <RuleTester
                                formikProps={updatedFormikProps}
                                parameterData={parameterData}
                                listData={listData}
                                conclusionFlagListData={conclusionFlagListData}
                                mode={actionType}
                                currentRule={currentRule}
                                rulesType={rulesType}
                                overrideLevel={overrideLevel}
                            />
                        )}
                    </div>
                );
            }}
        </Formik>
    );
};

export default EditSimpleRuleForm;
